import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import { baseURL } from "../../services/axios/eps";
import { toast } from "sonner";
import { createSection, updateSection } from "../../services/axios/apiManager";
import {
  formatDateForInput,
  getTodayDateString,
  getNextWeekDateString,
} from "../../services/dataManager";

const TournamentForm = ({
  id,
  selectedOption,
  navigate,
  gamesList,
  sectionData,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedGames, setSelectedGames] = useState([]);
  const [gameQuery, setGameQuery] = useState("");

  const formik = useFormik({
    initialValues: {
      heading: sectionData.heading || "",
      details: sectionData?.details || "",
      status: sectionData?.status || 1,
      games_included: sectionData?.games_included || 1,
      games_list: sectionData?.games_list || "",
      section_type: sectionData?.section_type || "",
      is_timed: sectionData?.is_timed || 1,
      start_date:
        formatDateForInput(sectionData?.start_date) || getTodayDateString(),
      end_date:
        formatDateForInput(sectionData?.end_date) || getNextWeekDateString(),
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      heading: Yup.string().required("heading is required!"),
      details: Yup.string()
        .required("Details are required!")
        .min(50, "Details must be at least 50 characters")
        .max(500, "Details cannot exceed 500 characters"),
      start_date: Yup.date()
        .required("Start date is required")
        .min(
          new Date(new Date().setHours(0, 0, 0, 0)),
          "Start date cannot be in the past.",
        ),
      end_date: Yup.date()
        .required("End date is required.")
        .min(Yup.ref("start_date"), "End date cannot be before start date."),
      games_list: Yup.array()
        .required("Game list is required, select atleast 1 game")
        .min(1, "Select atleast 1 game"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          ...values,
          section_type: selectedOption,
        };
        console.log("payload :", payload);
        let res;
        if (id) {
          res = await updateSection({ id, ...payload });
        } else {
          res = await createSection(payload);
        }
        if (res?.data?.success) {
          toast.success(res?.data?.message || "Section saved successfully");
          navigate("/home-page");
        } else {
          toast.error(res?.data?.message || "Failed to save section");
        }
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while saving");
      } finally {
        setLoading(false);
      }
    },
  });

  // // // Filter games list
  const filteredGames =
    gamesList.filter((game) => {
      if (!gameQuery) return true;
      const title = game.title.toLowerCase();
      const query = gameQuery.trim().toLowerCase();
      return title.includes(query);
    }) || [];

  // // // Add/Remove game from selection
  const handleSelectGame = (id) => {
    let temp = [];
    if (selectedGames.includes(id)) {
      temp = selectedGames.filter((item) => item !== id);
      setSelectedGames(temp);
      formik.setFieldValue("games_list", temp);
    } else {
      temp = [...selectedGames, id];
      setSelectedGames(temp);
      formik.setFieldValue("games_list", temp);
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      window.scroll({ top: 0, behavior: "smooth" });
    }
  }, [formik.submitCount, formik.errors]);

  useEffect(() => {
    if (sectionData) {
      setSelectedGames(sectionData?.games_list || []);
    }
  }, [sectionData]);

  return (
    <div className="align-item-center bg-dark mt-5 rounded">
      <Form onSubmit={formik.handleSubmit} className="p-3 rounded">
        <Row className="mb-4">
          <Col className="sm-12 md-12">
            <Form.Label>Heading*</Form.Label>
            <Form.Control
              type="text"
              name="heading"
              data-testid="heading"
              placeholder="Enter Heading"
              className="bg-dark input-placeholder-light text-light border-light py-4"
              {...formik.getFieldProps("heading")}
              isInvalid={formik.touched.heading && !!formik.errors.heading}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.heading}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="sm-12 md-12">
            <Form.Label>Details*</Form.Label>
            <Form.Control
              type="text"
              as={"textarea"}
              rows={4}
              placeholder="Enter Details"
              name="details"
              data-testid="details"
              className="bg-dark text-light border-light py-2 input-placeholder-light"
              {...formik.getFieldProps("details")}
              isInvalid={formik.touched.details && !!formik.errors.details}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.details}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="xs-12 sm-12 md-6">
            <Form.Label>Start Date*</Form.Label>
            <Form.Control
              type="date"
              name="start-date"
              data-testid="start-date"
              className="bg-dark text-light border-light py-4 input-placeholder-light"
              {...formik.getFieldProps("start_date")}
              isInvalid={
                formik.touched.start_date && !!formik.errors.start_date
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.start_date}
            </Form.Control.Feedback>
          </Col>
          <Col className="xs-12 sm-12 md-6">
            <Form.Label>End Date*</Form.Label>
            <Form.Control
              type="date"
              name="end-date"
              data-testid="end-date"
              className="text-light bg-dark border-light py-4 input-placeholder-light"
              {...formik.getFieldProps("end_date")}
              isInvalid={formik.touched.end_date && !!formik.errors.end_date}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.end_date}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <hr />
        <div>
          <p className="text-light font-bold">
            {`Select Games (${selectedGames?.length || 0} selected )`}
          </p>
          <span className="text-danger">{formik.errors.games_list}</span>
          <Form.Control
            type="text"
            name="game-search"
            placeholder="Search games..."
            data-testid="game-search"
            className="text-light bg-dark border-light py-4 input-placeholder-light"
            value={gameQuery}
            onChange={(e) => setGameQuery(e.target.value)}
          />
          <div className="flex bg-dark rounded mt-4">
            <div
              className="row w-100 rounded overflow-auto"
              style={{ minHeight: "100px", maxHeight: "600px" }}
            >
              {filteredGames?.length ? (
                filteredGames.map((game) => (
                  <div
                    className="col-xs-12 col-sm-12 col-md-4"
                    onClick={() => {
                      handleSelectGame(game.id);
                    }}
                  >
                    <div
                      className={`d-flex justify-content-between align-items-center rounded-lg cursor-pointer border-2 p-2 m-2 ${selectedGames?.includes(game.id) ? "border-2 border-indigo-600 bg-slate-800" : "border-2 bg-dark"}`}
                    >
                      <div className="d-flex align-items-center">
                        <img
                          src={`${baseURL}/uploads/file/${game?.icon}`}
                          crossOrigin="anonymous"
                          alt="game-icon"
                          style={{
                            height: "60px",
                            width: "60px",
                            borderRadius: "10px",
                            objectFit: "contain",
                          }}
                        />
                        <p className="font-bold ml-2">{game.title}</p>
                      </div>
                      {selectedGames?.includes(game.id) && (
                        <div className="text-indigo-600 dark:text-indigo-400">
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-light">No games to show</p>
              )}
            </div>
          </div>
        </div>
        <hr />
        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate("/home-page")}
            // disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? "Saving..." : id ? "Save Changes" : "Create Section"}
          </Button>
        </div>
      </Form>
    </div>
  );
};
export default TournamentForm;
